import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import CentralCalls from '../../centralCalls';
import './WipTabs.css';

const WipPhaseFilesDialog = ({
  open,
  onClose,
  phaseOid,
  selectedLotOid,
}) => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const hasRequiredFilters = useMemo(() => {
    return Boolean(phaseOid);
  }, [phaseOid]);

  const loadFiles = useCallback(async () => {
    if (!hasRequiredFilters) {
      setFiles([]);
      return;
    }

    const whereClause = {
      is_deleted: 0,
      phase_oid: phaseOid,
    };

    if (selectedLotOid) {
      whereClause.lot_oid = selectedLotOid;
    }

    setIsLoading(true);
    try {
      const data = await CentralCalls.fetchTableData('files', whereClause);
      const rows = Array.isArray(data) ? [...data] : [];
      rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setFiles(rows);
    } catch (error) {
      console.error(error);
      alert('Unable to load phase files. Please try again.');
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasRequiredFilters, phaseOid, selectedLotOid]);

  useEffect(() => {
    if (!open) {
      return;
    }

    loadFiles();
  }, [open, loadFiles]);

  const handleDownload = async (fileOid, fileName) => {
    try {
      const response = await fetch(`/api/files/${fileOid}/download`);
      if (!response.ok) {
        throw new Error('Download failed.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('Unable to download file.');
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Phase Files</DialogTitle>
      <DialogContent dividers>
        {!hasRequiredFilters && (
          <p>No phase selected.</p>
        )}

        {hasRequiredFilters && isLoading && <p>Loading files...</p>}

        {hasRequiredFilters && !isLoading && files.length === 0 && (
          <p>No files found for this phase.</p>
        )}

        {hasRequiredFilters && !isLoading && files.map((fileRow) => (
          <div className="card file-card" key={fileRow.oid}>
            <button
              type="button"
              onClick={() => handleDownload(fileRow.oid, fileRow.file_name)}
              className="file-link-button"
            >
              {fileRow.file_name}
            </button>

            {fileRow.description && String(fileRow.description).trim() !== '' && (
              <p className="file-description">{fileRow.description}</p>
            )}

            <p className="file-meta-row">
              <span className="note-user">By: {fileRow.uploaded_by || 'Unknown'}</span>
              <span className="note-date">
                Date: {fileRow.created_at ? new Date(fileRow.created_at).toLocaleDateString() : ''}
              </span>
            </p>
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WipPhaseFilesDialog;
